import { env } from '@/lib/env'
import { refreshSession } from '@/lib/auth-refresh'

export const AUTH_BASE_URL = env.authBaseUrl

/**
 * Fetch with a timeout to prevent hanging requests. An external AbortSignal
 * passed in init is also respected (either signal aborts the request).
 */
export async function fetchWithTimeout(
  input: RequestInfo,
  init?: RequestInit,
  timeoutMs = 10000,
): Promise<Response> {
  const timeoutController = new AbortController()
  const externalSignal = init?.signal

  const id = setTimeout(() => {
    timeoutController.abort()
  }, timeoutMs)

  if (externalSignal) {
    if (externalSignal.aborted) {
      timeoutController.abort()
    } else {
      externalSignal.addEventListener('abort', () => {
        timeoutController.abort()
      })
    }
  }

  try {
    const response = await fetch(input, {
      ...init,
      signal: timeoutController.signal,
    })
    return response
  } finally {
    clearTimeout(id)
  }
}

export async function refreshAccessTokenWithStatus(
  storedToken: string | null,
): Promise<{
  token: string | null
  email?: string | null
  status?: number
  error?: Error
}> {
  // Delegate to the shared single-flight core (lib/auth-refresh.ts) so this
  // SessionProvider path and apiClient.tryRefreshToken coalesce into one
  // /auth/refresh request and can't race-revoke each other's rotated Keychain
  // refresh token (native-oauth-pass Ch.1 review fix). The native header-refresh
  // and rotate-persist mechanics live there.
  const { accessToken, email, status, error } = await refreshSession(storedToken)
  return { token: accessToken, email, status, error }
}
